import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import { useIsAndroidApp } from "@/hooks/useLaunchContext";
import { LANDING } from "@/constants/testIds";
import TryForFreeBlock from "@/components/landing/TryForFreeBlock";
import { PromoBadge } from "@/components/landing/PromoBadge";

export default function Hero() {
  const { t } = useI18n();
  const isTwaApp = useIsAndroidApp();
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/13532875/pexels-photo-13532875.jpeg?auto=compress&cs=tinysrgb&w=1920"
          srcSet="https://images.pexels.com/photos/13532875/pexels-photo-13532875.jpeg?auto=compress&cs=tinysrgb&w=828 828w, https://images.pexels.com/photos/13532875/pexels-photo-13532875.jpeg?auto=compress&cs=tinysrgb&w=1920 1920w"
          sizes="100vw"
          alt="Vinyl turntable"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/85 to-background" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-28 sm:px-6 sm:py-36 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            {t("landing.badge")}
          </span>
          <br />
          <PromoBadge variant="hero" />

          <h1 className="mt-6 text-balance font-heading text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            {t("landing.heroTitle")}
          </h1>
        </motion.div>

        {/* Bloc "Composez votre musique" remonté juste sous le titre (demande admin 18/08/2026)
            pour qu'il soit visible immédiatement, avant le sous-titre et les CTA. */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
          <TryForFreeBlock />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="mx-auto mt-10 max-w-2xl text-balance text-base leading-relaxed text-muted-foreground md:text-lg">
            {t("landing.heroSubtitle")}
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button asChild size="lg" data-testid={LANDING.heroCta} className="group h-13 rounded-full px-8 text-base shadow-lg shadow-primary/20 transition-transform hover:-translate-y-1">
              <Link to="/create">
                {t("landing.heroCta")}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" data-testid={LANDING.heroSecondaryCta} className="h-13 rounded-full border-foreground/15 px-8 text-base">
              <Link to="/galerie">
                <PlayCircle className="mr-2 h-4 w-4" />
                {t("landing.heroSecondaryCta")}
              </Link>
            </Button>
          </div>

          {!isTwaApp && <p className="mt-6 font-heading text-sm font-semibold text-primary">{t("landing.heroNote")}</p>}
        </motion.div>
      </div>
    </section>
  );
}
